import { addDaysIso } from '@gozoki/tools'

export const SUPPLY_WINDOW_DAYS = [3, 5, 9] as const

export type SupplyWindowDays = (typeof SUPPLY_WINDOW_DAYS)[number]

// Fenêtres autorisées côté store-app : le dashboard boutique affiche 7 jours (jour courant au
// milieu). Constante distincte de SUPPLY_WINDOW_DAYS pour ne PAS ajouter de bouton « 7 » au sélecteur
// du back-office, qui itère sur SUPPLY_WINDOW_DAYS.
export const STORE_SUPPLY_WINDOW_DAYS = [3, 5, 7, 9] as const

export type StoreSupplyWindowDays = (typeof STORE_SUPPLY_WINDOW_DAYS)[number]

export type SupplyEventKind = 'delivery' | 'production_order' | 'stockout' | 'breakage'

export type SupplyStockoutCause = 'defective_stock' | 'depleted'

export interface SupplyHourPoint {
  t: string
  stock: number | null
  sales: number | null
  correctedSales: number | null
  forecastSales: number
  forecastStock: number | null
}

export interface SupplyEventProduct {
  label: string
  count: number
}

export interface SupplyEvent {
  t: string
  kind: SupplyEventKind
  count: number
  id: string
  rate?: number | null
  fifoScore?: number | null
  cause?: SupplyStockoutCause
  products?: SupplyEventProduct[]
  inventoryId?: number | null
}

export interface SupplyKpis {
  vpj: number
  vph: number
  sales: number
  gapPercent: number
  forecastElapsed: number
  correctedSales: number
  stock: number
  expectedLanding: number
  expectedLandingPercent: number
  expectedStock: number
  breakageRate: number
  breakageCount: number
  forecastBreakageCount: number
  forecastBreakageRate: number
  stockoutMissedUnits: number
  stockoutEvents: number
  stockoutRate: number | null
  forecastStockoutMissedUnits: number
  forecastStockoutRate: number | null
  salesWindow: number
  salesWindowPercent: number
  forecastSalesPastWindow: number
  forecastSalesWindow: number
  forecastSalesWindowPercent: number
}

export interface SupplyCategoryRow {
  categoryId: number
  label: string
  kpis: SupplyKpis
  engineCorrectionPercent: number
}

export interface SupplyArticleRow {
  productId: number
  label: string
  kpis: SupplyKpis
  engineCorrectionPercent: number
}

export interface SupplyPreviousForecast {
  anchorAt: string
  sales: (number | null)[]
}

export interface SupplyTimelineResponse {
  window: { start: string; end: string; now: string }
  hours: SupplyHourPoint[]
  previousForecast: SupplyPreviousForecast | null
  events: SupplyEvent[]
  kpis: SupplyKpis
  categories: SupplyCategoryRow[]
  articles: SupplyArticleRow[]
  undatedArticles: number
}

export interface SupplyTimelineParams {
  /** `null` reads the network view: the sum of every active boutique. */
  storeId: number | null
  days: SupplyWindowDays
  scope?: 'category' | 'product'
  targetId?: number
  /** Jour passé (`YYYY-MM-DD`, Paris) : l'écran tel qu'il était le soir de ce jour. Absent = maintenant. */
  date?: string
}

// store-app : pas de storeId (dérivé du token `store`), fenêtre élargie (7 jours), pas d'historique.
export type StoreSupplyTimelineParams = Omit<SupplyTimelineParams, 'storeId' | 'days' | 'date'> & {
  days: StoreSupplyWindowDays
}

/**
 * Profondeur de l'historique Supply, en jours. Le profil horaire des prévisions n'est conservé
 * que `FORECAST_HOUR_PROFILE_WEEKS` (2 semaines, `forecastRetention.service` du backend) : au-delà,
 * la courbe prévue serait vide. À relever avec cette rétention, jamais au-dessus.
 */
export const SUPPLY_HISTORY_DAYS = 13

export interface SupplyHistoryBounds {
  /** Premier jour sélectionnable (`YYYY-MM-DD`). */
  earliest: string
  /** Hier : aujourd'hui, c'est le direct. */
  latest: string
}

/**
 * Jours rejouables par l'historique Supply, `today` étant le jour civil de Paris. Le jour le plus
 * ancien lu doit garder son profil horaire : le premier jour de la fenêtre centrée, mais aussi le
 * premier des `SUPPLY_LOSS_WINDOW_DAYS` jours qui précèdent le jour choisi (KPI prévision vs réel).
 */
export const supplyHistoryBounds = (days: number, today: string): SupplyHistoryBounds => {
  const lookBack = Math.max((days - 1) / 2, SUPPLY_LOSS_WINDOW_DAYS)
  return {
    earliest: addDaysIso(today, lookBack - SUPPLY_HISTORY_DAYS),
    latest: addDaysIso(today, -1),
  }
}

export const SUPPLY_LOSS_WINDOW_DAYS = 4
export const SUPPLY_LOSS_FORECAST_DAYS = 4
export const SUPPLY_STOCKOUT_FORECAST_DAYS = 4
